import { Modal } from '@tractian/ui'
import type { ReactNode } from 'react'

import { DEV_TOOLS, type DevToolId } from '@/features/dev-tools/domain/dev-tools'

export type DevToolsModalProps = {
  open: boolean
  activeTool: DevToolId | null
  /**
   * The active tool's panel — its `Modal.Body` + `Modal.Footer`. The tool owns
   * both so its state (a staged draft, the matrix form) can drive buttons that
   * live in the footer, outside the scrolling body.
   */
  panel: ReactNode
  onOpenChange: (open: boolean) => void
  onSelectTool: (tool: DevToolId | null) => void
}

export function DevToolsModal({
  open,
  activeTool,
  panel,
  onOpenChange,
  onSelectTool
}: DevToolsModalProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {/* `h-fit` overrides the kit's fixed `h-[91.666667%]` so the modal hugs its
          content; max-h + the body's own overflow-y-auto keep a tall panel (the
          matrix form) scrollable instead of clipped on short viewports. */}
      <Modal.Content classes={{ content: 'h-fit max-h-[90vh] w-[640px] max-w-[100vw]' }}>
        <Modal.Header size="small">
          {activeTool === null ? 'Dev tools' : DEV_TOOLS.find(tool => tool.id === activeTool)?.name}
        </Modal.Header>
        {activeTool === null ? (
          <Modal.Body>
            <div className="flex flex-col divide-y divide-gray-100">
              {DEV_TOOLS.map(tool => (
                <button
                  key={tool.id}
                  type="button"
                  onClick={() => onSelectTool(tool.id)}
                  className="flex items-center justify-between gap-4 py-3 text-left hover:bg-gray-50"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-900">{tool.name}</span>
                    <span className="block truncate text-xs text-gray-500">{tool.description}</span>
                  </span>
                  <kbd className="shrink-0 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 font-mono text-xs text-gray-600">
                    {tool.shortcut}
                  </kbd>
                </button>
              ))}
            </div>
          </Modal.Body>
        ) : (
          panel
        )}
      </Modal.Content>
    </Modal>
  )
}
